import { useEffect, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { addCalendarDays, fmtDate, fmtDateTime, NOTICE_WINDOW_CALENDAR_DAYS } from '../../../lib/util';
import type { Case } from '../../../domain/initial-cases';
import { StatusBadge } from '../../../components/StatusBadge';
import { DeadlineIndicator } from '../../../components/DeadlineIndicator';
import { DraftTextInput } from '../../../components/DraftTextInput';
import { getCurrentUser, isAssignedAnalyst, type CurrentUser } from '../../../auth/useUser';
import { listUsers, type AdminUser } from '../../../api/users';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import { isNotifiableDecision, undeliveredNoticeReason, useLifecycleActions } from '../../../state/actions/lifecycle';
import { getCaseConfirmations, type CaseConfirmations } from '../../../api/settlement';
import { approverLine, decidedOnLine, finalChangedHold, recommendationLine } from './DecisionTab';

/**
 * #681 — who may hold a case as its analyst.
 *
 * Exactly the roles whose holders `canEdit` in `auth/useUser.ts`, which is the
 * same pair the two staff create doors already stamp: an admin who creates a
 * case becomes its analyst today, so an admin the portal refuses to REassign to
 * would be a directory that cannot express a state the portal already produces.
 *
 * Disabled accounts are dropped because assigning work to one is assigning it
 * to nobody, and a DELETED account is refused outright by `resolvePrincipals()`
 * (#463) — `/admin/users` reads Keycloak, which no longer holds a deleted
 * account at all, so the tombstoned case needs no filter here.
 */
function isAssignable(u: AdminUser): boolean {
  return u.enabled && (u.role === 'bcid-analyst' || u.role === 'bcid-admin');
}

/**
 * What the OA/VA Agent field (the case assignee) reads, and why it is not simply a name.
 *
 * `cases.analyst_id` holds a server-generated `users.id`, and NOTHING the SPA
 * can reach maps one back to a person: `/admin/users` is keyed on the Keycloak
 * id and is admin-only besides, `/me` is self-scoped, and `resolveActorName()`
 * only resolves the `actorName` the API joins onto AUDIT rows. So the name has
 * to come from the server, and `analystName` is the projection that sends it.
 *
 * `(you)` is not decoration. The reader being the assigned analyst is the whole
 * dual-control question on this case, and it is answered by comparing in the
 * SERVER id space — `isAssignedAnalyst` against `user.serverId`, never
 * `user.id`, which is the Keycloak sub.
 *
 * The raw id is the last resort, for the one window where the name has not
 * arrived: `POST /cases` returns the inserted columns without the projection,
 * so a case this client just created has an `analystId` and no name until the
 * next read. Showing the id beats showing nothing.
 */
function assignedAnalystLabel(c: Case, user: CurrentUser): string {
  if (!c.analystId) return 'Unassigned';
  if (isAssignedAnalyst(c.analystId, user)) return `${user.name} (you)`;
  return c.analystName ?? c.analystId;
}

export function OverviewTab({ c }: { c: Case }) {
  const { now, updateCase } = useAppData();
  const { setModal } = useUIState();
  const { receiveAppeal, recordCure, issueFinalDetermination, syncToSettlement, requestDeletion, confirmDeletion } = useLifecycleActions();
  const user = getCurrentUser()!;
  // #781 review — the reader made the case's current recommendation (users.id
  // against users.id, never the sub).
  const youRecommendedThis = !!c.recommendedBy && !!user.serverId && c.recommendedBy === user.serverId;
  const canEdit = user.canEdit;
  const canApprove = user.canApprove;
  const canAdmin = user.canAdmin;

  // #681 — THE ANALYST-ASSIGNMENT DIRECTORY, fetched ONLY when an admin opens
  // the picker. Three reasons it is not loaded on mount: `GET /admin/users` is
  // admin-only and would 403 for the analysts who are the main readers of this
  // tab; it fans out to Keycloak once per staff account, so paying for it on
  // every case open would be the expensive call on the cheapest screen; and it
  // 503s until the bcid-admin-svc credential is provisioned, which is a state
  // this tab should report when somebody asks to reassign rather than on sight.
  const [directory, setDirectory] = useState<AdminUser[] | null>(null);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  function openPicker(): void {
    setPicking(true);
    if (directory) return;
    setDirectoryError(null);
    listUsers()
      .then((all) => setDirectory(all.filter(isAssignable)))
      .catch(() => setDirectoryError('Could not load the OA/VA Agent list.'));
  }

  function assignTo(keycloakId: string, analystName: string): void {
    setPicking(false);
    // THE KEYCLOAK `sub`, NOT `user.serverId` (CLAUDE.md B-2). `cases.analyst_id`
    // FKs to the server-generated `users.id`, and `resolvePrincipals()` in the
    // API translates the sub into it. Writing the serverId here would be
    // rejected as "not a known user" — the two ID spaces run the other way for
    // WRITES than they do for the comparisons `isAssignedAnalyst` makes.
    //
    // ONE PATCH for one logical action (H-1): same-tick `updateCase` calls are
    // serialised per case, so this must not be split into an assignment write
    // and a separate audit write.
    //
    // The audit code stays CASE_UPDATED — assignment is not a new kind of event
    // and a new code would put nine files into lockstep for it. The detail is
    // what makes the row answer "assigned to whom": `changes` mirrors the
    // default detail the override replaces, so a reader of the audit tab sees
    // the same shape every other PATCH writes.
    updateCase(c.id, {
      analystId: keycloakId,
      action: {
        code: 'CASE_UPDATED',
        detail: {
          changes: ['analystId'],
          assignedAnalystName: analystName,
          previouslyAssigned: c.analystId ?? null,
        },
      },
    });
  }

  // Display confirmations (#112): only fetched once an EID exists — a case
  // that was never synced has no CDRs and the endpoint would just return zero.
  const [confirmations, setConfirmations] = useState<CaseConfirmations | null>(null);
  useEffect(() => {
    if (!c.eid) { setConfirmations(null); return; }
    let cancelled = false;
    getCaseConfirmations(c.id)
      .then((r) => { if (!cancelled) setConfirmations(r); })
      .catch(() => { if (!cancelled) setConfirmations(null); });
    return () => { cancelled = true; };
  }, [c.id, c.eid]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Registrant Information</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          {/* #688 — DRAFT-then-commit, not a PATCH per keystroke. This field and
              the two #693 removed below it were bound straight to server state
              with `onChange={updateCase}`,
              which made every character a full If-Match PATCH: the field could
              not show a character until its round trip finished, so fast typing
              lost characters and forked the per-case chain into 409s. The
              component holds the draft and commits once, on blur or Enter, only
              when the value changed. Escape reverts. */}
          <div><label htmlFor="ov-registrant" style={{ fontSize: 11, color: C.textMuted }}>Registrant Name</label><DraftTextInput id="ov-registrant" style={styles.input} value={c.registrant} onCommit={v => updateCase(c.id, { registrant: v })} /></div>
          {/* #693 — no "Onboarding Agent" and no "OA Email". 382 is the
              Onboarding Agent on every application (#692), so both fields
              asked an analyst to type 382's own details onto the case — and
              the email was not decorative while they did it: it addressed
              every case notice and gated entry to vetting. Notices now go to
              the registrant's own authorizer contact, billing contact as
              fallback, read from the submission and edited on the Onboarding
              tab (`notificationContact` in state/actions/lifecycle.ts). The
              columns still exist and the API still returns them; nothing
              writes them. */}
          {/* #692 — no "382 Communications is acting as Onboarding Agent"
              checkbox. 382 is the Onboarding Agent on every application, so
              the box asked the analyst to restate a constant, and the answer
              chose the vetting SLA term behind their back: 15 business days
              ticked, 10 unticked. Intake-promoted cases never set it, so the
              majority of production cases were quietly on the short clock.
              Every case now gets 15 business days; the server decides it and
              nothing on this tab can move it. */}
          {/* #681 — WHO IS VETTING THIS CASE, and the only control in the
              portal that can answer it.

              The API has accepted `analystId` on PATCH /cases/:id since the
              beginning and fires the "a case is assigned to me" mail off it,
              but nothing in the SPA ever sent one: the two staff create doors
              stamped their creator and the intake promote — the door EVERY
              production case comes through — left the column NULL for the
              case's whole life. An unassigned case is not a cosmetic gap.
              `dual_control_distinct_principals` compares against this column,
              so on an unassigned case the approver who did the vetting
              themselves saw a live "Approve: Successful Vet" and no warning;
              "Assigned to me" never matched it; and every notice addressed to
              "the assigned analyst" addressed nobody. */}
          <div data-testid="assigned-analyst">
            <label style={{ fontSize: 11, color: C.textMuted }} id="ov-analyst-label">OA/VA Agent</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <span
                style={{ fontSize: 13, color: c.analystId ? C.text : C.textDim }}
                data-testid="assigned-analyst-name"
              >
                {assignedAnalystLabel(c, user)}
              </span>
              {/* Analyst or admin only. An approver or a legal reviewer picking
                  up the vetting is the dual-control breach this field exists to
                  make visible, so the control that would cause one is not
                  offered to them — the CHECK constraint still refuses it if
                  they reach the API another way. */}
              {canEdit && !c.analystId && (
                <button
                  style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }}
                  onClick={() => assignTo(user.id, user.name)}
                >Assign to me</button>
              )}
              {canAdmin && !picking && (
                <button
                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                  onClick={openPicker}
                >{c.analystId ? 'Reassign…' : 'Assign…'}</button>
              )}
            </div>
            {canAdmin && picking && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {directoryError
                  ? <span style={{ fontSize: 12, color: C.danger }}>{directoryError}</span>
                  : !directory
                    ? <span style={{ fontSize: 12, color: C.textMuted }}>Loading OA/VA Agents…</span>
                    : (
                      <select
                        style={styles.select}
                        aria-labelledby="ov-analyst-label"
                        defaultValue=""
                        onChange={(e) => {
                          const chosen = directory.find((u) => u.keycloakId === e.target.value);
                          if (chosen) assignTo(chosen.keycloakId, chosen.name);
                        }}
                      >
                        <option value="">Select an OA/VA Agent…</option>
                        {directory.map((u) => (
                          <option key={u.keycloakId} value={u.keycloakId}>{u.name} ({u.role})</option>
                        ))}
                      </select>
                    )}
                <button
                  style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                  onClick={() => setPicking(false)}
                >Cancel</button>
              </div>
            )}
          </div>
          {/* #776 — who recommended and who decided, by name, in the same words
              the Decision tab uses (the helpers are shared). Before a decision
              only the recommendation can exist; once decided, the approver
              joins it. #797 — a decision consumes its recommendation, so once
              decided the line is the one the decision was taken on, from the
              history (decidedOnLine); a current recommendation beside it was
              made since, for the next decision. */}
          <div data-testid="overview-decision-people">
            <label style={{ fontSize: 11, color: C.textMuted }}>Recommendation &amp; Decision</label>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {c.decision ? (
                <>
                  <div>{decidedOnLine(c) ?? recommendationLine(c) ?? 'No recommendation on record'}</div>
                  <div>{approverLine(c)}</div>
                  {decidedOnLine(c) && recommendationLine(c) && (
                    <div data-testid="overview-current-recommendation">Since the decision: {recommendationLine(c)}</div>
                  )}
                </>
              ) : (
                <div>{recommendationLine(c) ?? 'No recommendation yet'}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Timelines & Deadlines</h3>
        {c.decision ? (
          // sla_deadline persists after the decision (#137). Once decided, the live
          // countdown reads as pending work, so show the fixed outcome instead:
          // met if the decision landed on or before the deadline, missed otherwise.
          (() => {
            const missed = !!(c.slaDeadline && c.decisionDate && new Date(c.decisionDate) > new Date(c.slaDeadline));
            return (
              <div style={{ fontSize: 12, color: missed ? C.danger : C.success, marginTop: 4 }}>
                {missed
                  ? `✗ SLA missed — decision ${fmtDate(c.decisionDate)}`
                  : `✓ Decision issued ${fmtDate(c.decisionDate)} — within SLA`}
              </div>
            );
          })()
        ) : (
          <DeadlineIndicator deadline={c.slaDeadline} label="SLA (15 biz days)" now={now} />
        )}
        {isNotifiableDecision(c.decision) && !c.notificationSentAt && <DeadlineIndicator deadline={addCalendarDays(c.decisionDate!, NOTICE_WINDOW_CALENDAR_DAYS).toISOString()} label="30-day notification" now={now} />}
        {/* #543 — a recorded notice the mail producer refused is not a notified registrant.
            Saying "✓ Registrant notified" for one is the claim the bug made; the reason
            comes from the one reading of the producer's gates. */}
        {c.notificationSentAt && (undeliveredNoticeReason(c)
          ? <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>⚠ Notification RECORDED {fmtDate(c.notificationSentAt)} but not delivered — see the Notifications tab</div>
          : <div style={{ fontSize: 12, color: C.success, marginTop: 4 }}>✓ Registrant notified {fmtDate(c.notificationSentAt)} via {c.notificationMethod}</div>)}
        {c.cureDeadline && c.cureStatus === 'OPEN' && <DeadlineIndicator deadline={c.cureDeadline} label="Cure window (12 biz days)" now={now} />}
        {c.appealDeadline && c.status === 'APPEAL' && <DeadlineIndicator deadline={c.appealDeadline} label="Appeal reassessment (90 cal days)" now={now} />}
        {c.revetDueAt && <DeadlineIndicator deadline={c.revetDueAt} label="Annual re-vet due" now={now} />}
        {c.settlementChangeDeadline && c.settlementSyncStatus !== 'SYNCED' && <DeadlineIndicator deadline={c.settlementChangeDeadline} label="Settlement sync (24hr)" now={now} />}
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Quick Actions</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {isNotifiableDecision(c.decision) && !c.notificationSentAt && (canEdit || canApprove) && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => setModal('send-notification')}>Send Notification</button>}
          {/* #626 — BOTH halves of the cure window, for the reason #587 split
              them on the Cure & Appeals page: a cure and a dispute are two
              events, and the discriminator is which button an approver
              presses. Offering the appeal alone here does not make this
              surface neutral — it makes it a surface where the only way to
              record a registrant's response is to record it as an appeal,
              which is the defect #587 removed, still reachable from the tab
              an analyst actually has open. Same guard and same permission as
              the appeal button beside it, and cure first, as there. */}
          {c.cureStatus === 'OPEN' && canApprove && <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => recordCure(c.id)}>Record Cure</button>}
          {c.cureStatus === 'OPEN' && canApprove && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => receiveAppeal(c.id)}>Record Appeal</button>}
          {/* #781 — Final: Changed is a decision, and the API refuses one on a
              case with no current recommendation; the agent makes it on the
              Decision tab. Final: Unchanged needs none. */}
          {/* #781 review — and not to the approver who RECOMMENDED the case
              (reassigned away from them since): the API refuses both
              determinations as approver_is_not_recommender, and the breach row
              pages on-call. Compared in the serverId space, as DecisionTab does
              (CLAUDE.md B-2). */}
          {c.status === 'APPEAL' && canApprove && !isAssignedAnalyst(c.analystId, user) && youRecommendedThis && <span style={{ fontSize: 12, color: C.textMuted, alignSelf: 'center' }} data-testid="final-you-recommended">You recommended this case — a different approver must issue the final determination</span>}
          {/* #797 — held, the same way as on Cure & Appeals (finalChangedHold),
              until the agent has recommended again since the decision and,
              unless the case is grandfathered, its KYC minimums are met. */}
          {c.status === 'APPEAL' && canApprove && !isAssignedAnalyst(c.analystId, user) && !youRecommendedThis && (() => {
            const hold = finalChangedHold(c);
            if (hold?.reason === 'recommendation') {
              return <span style={{ fontSize: 12, color: C.textMuted, alignSelf: 'center' }} data-testid="final-changed-needs-recommendation">Final: Changed needs a new recommendation from the OA/VA Agent, made since the decision (Decision tab)</span>;
            }
            return (
              <>
                <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall, ...(hold ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }} disabled={!!hold} aria-describedby={hold ? 'final-changed-kyc-hold' : undefined} onClick={() => issueFinalDetermination(c.id, 'CHANGED')}>Issue Final: Changed</button>
                {hold && (
                  <span id="final-changed-kyc-hold" style={{ fontSize: 12, color: C.warn, alignSelf: 'center' }} data-testid="final-changed-kyc-hold">
                    Held until every required KYC check meets its evidence minimum. Still short: {hold.unmet.map(u => `${u.label} (${u.progress})`).join('; ')}
                  </span>
                )}
              </>
            );
          })()}
          {c.status === 'APPEAL' && canApprove && !isAssignedAnalyst(c.analystId, user) && !youRecommendedThis && <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => issueFinalDetermination(c.id, 'UNCHANGED')}>Issue Final: Unchanged</button>}
          {c.settlementSyncStatus === 'PENDING_SYNC' && (canEdit || canApprove) && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => syncToSettlement(c.id)}>Sync to Settlement</button>}
          {c.status === 'SUCCESSFUL' && <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => setModal('submit-change')}>Submit Change</button>}
          {c.status === 'SUCCESSFUL' && canApprove && <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => requestDeletion(c.id)}>Request Deletion</button>}
          {c.status === 'PENDING_DELETION' && canAdmin && <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => confirmDeletion(c.id)}>Confirm Deletion</button>}
        </div>
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Settlement Platform</h3>
        <div style={{ fontSize: 13 }}>
          <div>EID: <span style={{ fontFamily: 'monospace', color: c.eid ? C.goldText : C.textDim, fontWeight: c.eid ? 600 : 400 }}>{c.eid || 'Not generated'}</span></div>
          <div>Sync Status: {c.settlementSyncStatus ? <StatusBadge status={c.settlementSyncStatus} /> : '—'}</div>
          <div>Last Sync: {fmtDateTime(c.settlementLastSync)}</div>
        </div>
        {confirmations && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }} data-testid="display-confirmations">
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Display Confirmations</div>
            <div style={{ fontSize: 13 }}>
              <div>Confirmed calls: <strong style={{ color: confirmations.confirmationCount > 0 ? C.success : C.textDim }}>{confirmations.confirmationCount}</strong></div>
              <div>Last confirmed: {confirmations.lastConfirmedAt ? fmtDateTime(confirmations.lastConfirmedAt) : '—'}</div>
              {confirmations.confirmationCount === 0 && (
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>No branded calls have been confirmed on a handset yet.</div>
              )}
            </div>
            {confirmations.recent.length > 0 && (
              <table style={{ ...styles.table, marginTop: 8 }} data-testid="confirmation-recent">
                <thead><tr>
                  <th style={styles.th}>Call Time</th>
                  <th style={styles.th}>TSP</th>
                  <th style={styles.th}>Claims</th>
                  <th style={styles.th}>DIR ID</th>
                </tr></thead>
                <tbody>
                  {confirmations.recent.map((r, i) => (
                    <tr key={`${r.callAt}-${i}`}>
                      <td style={styles.td}>{fmtDateTime(r.callAt)}</td>
                      <td style={styles.td}>{r.tspName ?? '—'}</td>
                      <td style={styles.td}>{r.claimsRequested ?? '—'} → {r.claimsPassed ?? '—'}</td>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{r.dirid ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
